class CartDrawer extends HTMLElement {
  constructor() {
    super();

    /* a11y-p6-2026-10-05: the stock keyup Escape close is retired. Escape is handled once, in cwEscape (keydown, bubble
       phase), so a nested component such as the cart note gets first refusal (Codex P2-1). */
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    /* a11y-p5-2026-10-05: focus containment (2.4.3) that survives a second render of the drawer (the free
       bar script repaints #CartDrawer after an add) and an empty drawer. Tab and Shift+Tab
       cycle through the drawer's visible controls, Escape closes from anywhere while open,
       and focus that falls out of a re-render goes back to the dialog. */
    this.cwKeydown = this.cwKeydown.bind(this);
    this.cwEscape = this.cwEscape.bind(this);
  }

  /* a11y-p6-2026-10-05: document listeners and observers exist only while the element is in the page and are removed in
     disconnectedCallback (Codex P3-4). The re-render observer watches this element, not #CartDrawer, so it
     also survives a replacement of #CartDrawer itself (Codex P1-6, which did not reproduce). */
  connectedCallback() {
    document.addEventListener('keydown', this.cwKeydown, true);
    document.addEventListener('keydown', this.cwEscape);
    if (window.MutationObserver && !this.cwMo) {
      this.cwMo = new MutationObserver(() => this.cwRefocus());
      this.cwMo.observe(this, { childList: true, subtree: true });
    }
    this.cwRegister(); // a11y-p6-1-2026-10-05
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.cwKeydown, true);
    document.removeEventListener('keydown', this.cwEscape);
    if (this.cwMo) { this.cwMo.disconnect(); this.cwMo = null; }
    if (window.CwModalStack) window.CwModalStack.unregister('cart', this); // a11y-p6-1-2026-10-05
  }

  cwDialog() {
    return this.querySelector('.drawer__inner[role="dialog"]') || this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
  }

  cwFocusables() {
    const scope = this.querySelector('#CartDrawer') || this;
    return Array.from(scope.querySelectorAll('summary, a[href], button:enabled, [tabindex]:not([tabindex^="-"]), input:not([type=hidden]):enabled, select:enabled, textarea:enabled, iframe'))
      .filter((el) => el.getClientRects().length > 0 && getComputedStyle(el).visibility !== 'hidden' && !el.closest('[hidden], [aria-hidden="true"]'));
  }

  cwRefocus() {
    if (!this.classList.contains('active') || this.cwSuspended) return;
    const a = document.activeElement;
    if (a && a !== document.body && this.contains(a)) return;
    if (this.cwOtherModal(a)) return;
    const t = this.cwDialog();
    if (t) t.focus({ preventScroll: true });
  }

  cwOtherModal(el) {
    // focus inside some other modal dialog (for example the email signup popup) is left alone
    const m = el && el.closest ? el.closest('[aria-modal="true"]') : null;
    return !!(m && !this.contains(m));
  }

  /* a11y-p6-2026-10-05: Escape in the bubble phase. A handler deeper in the drawer that consumed the key (defaultPrevented)
     wins; an open disclosure inside the drawer (the cart note) is closed first and the drawer stays open;
     otherwise the drawer closes and focus returns to its trigger. */
  cwEscape(event) {
    if (event.key !== 'Escape' || event.defaultPrevented || event.isComposing) return;
    if (!this.classList.contains('active') || this.cwSuspended) return;
    const a = document.activeElement;
    if (this.cwOtherModal(a)) return;
    const det = a && a.closest ? a.closest('details[open]') : null;
    if (det && this.contains(det)) {
      event.preventDefault();
      det.removeAttribute('open');
      const sum = det.querySelector('summary');
      if (sum) { sum.setAttribute('aria-expanded', 'false'); sum.focus(); }
      return;
    }
    event.preventDefault();
    this.close();
  }

  /* a11y-p6-1-2026-10-05: modal stack through the shared manager (assets/cw-modal-stack.js, loaded by header.liquid
     before this file). The drawer registers itself. Before it opens, the manager closes a visible third party
     popup (the email signup) through the popup's own close control and waits until it is hidden; while
     another modal is above the drawer it suspends the drawer (inert first, then aria-hidden, trap and refocus
     paused through cwSuspended) and restores it, removing only what it added, with focus on the Close button
     once that modal is gone by any path. Replaces the P6 per dialog observer and its snapshot of the dialogs
     that happened to be open first (Codex P6 review, P1 and P2). */
  cwRegister() {
    const S = window.CwModalStack;
    if (!S) return;
    S.register('cart', {
      root: this,
      isOpen: () => this.classList.contains('active'),
      close: () => this.close(),
      focusClose: () => {
        const c = this.cwFocusables().find((el) => el.classList.contains('drawer__close')) || this.cwDialog();
        if (c) c.focus();
      },
      focusInside: () => { const d = this.cwDialog(); if (d) d.focus({ preventScroll: true }); },
      onSuspend: () => { this.cwSuspended = true; },
      onResume: () => { this.cwSuspended = false; }
    });
  }

  cwKeydown(event) {
    if (event.key !== 'Tab') return;
    if (!this.classList.contains('active') || this.cwSuspended) return;
    if (this.cwOtherModal(document.activeElement)) return;
    const f = this.cwFocusables();
    const dialog = this.cwDialog();
    const a = document.activeElement;
    if (!f.length) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (dialog) dialog.focus();
      return;
    }
    const first = f[0];
    const last = f[f.length - 1];
    if (!a || a === document.body || !this.contains(a)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      (event.shiftKey ? last : first).focus();
      return;
    }
    if (!event.shiftKey && a === last) {
      event.preventDefault();
      event.stopImmediatePropagation();
      first.focus();
    } else if (event.shiftKey && (a === first || a === dialog)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      last.focus();
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-link');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink)
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  /* a11y-p6-1-2026-10-05: a visible third party popup is closed first, then the drawer opens (CwModalStack.prepare runs
     the callback in the same task when nothing has to be closed, so the usual path is unchanged) */
  open(triggeredBy) {
    const S = window.CwModalStack;
    if (S) S.prepare('cart', () => this.cwOpenNow(triggeredBy));
    else this.cwOpenNow(triggeredBy);
  }

  cwOpenNow(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
      // a11y-p6-2026-10-05: initial focus on the next animation frame after the drawer becomes active (Codex P2-2)
      this.cwFocusSoon(0);
    });
    // a11y-p5-2026-10-05: focus lands inside even when no transitionend fires (reduced motion, re-render).
    // a11y-p6-2026-10-05: kept only as a fallback check behind cwFocusSoon.
    setTimeout(() => this.cwRefocus(), 700);
    if (window.CwModalStack) window.CwModalStack.opened('cart'); // a11y-p6-1-2026-10-05: pushed on the stack in open order

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    document.body.classList.add('overflow-hidden');
  }

  cwFocusSoon(tries) {
    (window.requestAnimationFrame || ((fn) => setTimeout(fn, 16)))(() => {
      if (!this.classList.contains('active') || this.cwSuspended) return;
      this.cwRefocus();
      const a = document.activeElement;
      if ((!a || !this.contains(a)) && tries < 10) this.cwFocusSoon(tries + 1);
    });
  }

  close() {
    if (window.CwModalStack) window.CwModalStack.closed('cart'); // a11y-p6-1-2026-10-05
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') && this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      sectionElement.innerHTML =
          this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    }));

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
